import React, { useMemo, useCallback } from 'react';
import { Container } from 'vendors/semantic-ui';

import { SidebarLayout } from 'modules/shared/layouts';
import { KeyValueFilter } from 'modules/shared/models';
import { readCMSValue, getDefault } from 'modules/shared-cms/utils';

import {
    DressFilterList,
    DressSortList,
    DressList,
    DressThumbProps,
    DressSortType,
    DressSortTypeValues,
    DressThumbMetaProps,
} from '..';
import { FilterableDressListProps } from './filterable-dress-list.type';

export const FilterableDressList: React.FC<FilterableDressListProps> = ({
    models,
    filters,
    handleFiltersSelection,
    ...rest
}: FilterableDressListProps) => {
    const filteredModels = useMemo<DressThumbProps[]>(() => {
        let result = [...models];
        filters.forEach(element => {
            if (element.values && element.values.length > 0) {
                result = result.filter(model =>
                    element.values.some(f => {
                        if (!f || element.id === 'sort') {
                            return true;
                        }

                        if (element.id === 'productPrice') {
                            const productPriceValue = getDefault(readCMSValue<number>(model.data.productPrice), 0);
                            if (element.values.length === 1) {
                                return productPriceValue >= Number(element.values[0]);
                            }
                            if (element.values.length === 2) {
                                return (
                                    productPriceValue >= Number(element.values[0]) &&
                                    productPriceValue <= Number(element.values[1])
                                );
                            }

                            return true;
                        }

                        if (element.id === 'selections') {
                            if (!model.data.selections || !model.data.selections.iv) {
                                return false;
                            }

                            return model.data.selections.iv.some(s => s.id.toUpperCase() === f.toUpperCase());
                        }

                        if (
                            ['type', 'longueur', 'manches', 'matiere', 'coupes', 'decollete'].indexOf(element.id) === -1
                        ) {
                            return true;
                        }

                        const elementVal = readCMSValue<string | string[] | number>(model.data[element.id]);
                        if (Array.isArray(elementVal)) {
                            return getDefault<string[]>(elementVal, []).some(e => e.toUpperCase() === f.toUpperCase());
                        }

                        const elementValStr = elementVal ? `${elementVal}` : '';
                        return elementValStr.toUpperCase() === f.toUpperCase();
                    }),
                );

                if (element.id === 'sort' && element.values && element.values.length > 0) {
                    switch (Number(element.values[0]) as DressSortType) {
                        case DressSortTypeValues.LOWER_PRICE:
                            result = result.sort((a, b) => a.data.productPrice.iv - b.data.productPrice.iv);
                            break;
                        case DressSortTypeValues.UPPER_PRICE:
                            result = result.sort((a, b) => b.data.productPrice.iv - a.data.productPrice.iv);
                            break;
                        case DressSortTypeValues.NAME:
                            result = result.sort((a, b) => (a.data.thumbTitle.iv > b.data.thumbTitle.iv ? 1 : -1));
                            break;
                        case DressSortTypeValues.UNKNOWN:
                        default:
                            break;
                    }
                }
            }
        });
        return result;
    }, [models, filters]);

    const priceList = useMemo(() => {
        return models.map(m => getDefault(readCMSValue(m.data.productPrice), 0));
    }, [models]);

    const computedFilters = useMemo(() => {
        return filters ? (filters.filter(q => q.id !== 'sort') as KeyValueFilter<keyof DressThumbMetaProps>[]) : [];
    }, [filters]);

    const computedSorts = useMemo(() => {
        const sort = filters ? filters.find(q => q.id === 'sort') : undefined;
        if (sort && sort.values && sort.values.length > 0) {
            return Number(sort.values[0]) as DressSortType;
        }

        return undefined;
    }, [filters]);

    const localHandleFiltersSelection = useCallback(
        updatedFilters => {
            handleFiltersSelection([...updatedFilters, ...filters.filter(f => f.id === 'sort')]);
        },
        [handleFiltersSelection, filters],
    );

    const localHandleSortSelection = useCallback(
        selectedSort => {
            handleFiltersSelection([{ id: 'sort', values: [selectedSort] }, ...filters.filter(f => f.id !== 'sort')]);
        },
        [handleFiltersSelection, filters],
    );

    return (
        <SidebarLayout
            sideComponent={
                <DressFilterList
                    filters={computedFilters}
                    handleFiltersSelection={localHandleFiltersSelection}
                    {...rest}
                    minPrice={Math.min(...priceList)}
                    maxPrice={Math.max(...priceList)}
                />
            }
            contentComponent={
                <Container>
                    <DressList models={filteredModels} fullWidth>
                        <DressSortList selectedValue={computedSorts} handleSortSelection={localHandleSortSelection} />
                    </DressList>
                </Container>
            }
        />
    );
};
